// License service for Vandromeda API communication
import debug from "../debug";
import {
  LicenseValidationResponse,
  LicenseActivationResponse,
  LicenseDeactivationResponse,
  LicenseTier,
} from "../types/licenseTypes";

// Vandromeda API base URL
const VANDROMEDA_API_BASE = "https://www.vandromeda.com/api";

// Local storage keys
const LICENSE_KEY_STORAGE = "trp_license_key";
const ROUTE_COUNT_STORAGE = "trp_daily_route_count";
const ROUTE_COUNT_DATE_STORAGE = "trp_route_count_date";

/**
 * Get the current domain for license validation
 */
const getCurrentDomain = (): string => {
  if (typeof window !== "undefined") {
    return window.location.hostname;
  }
  return "localhost";
};

/**
 * Store license key in localStorage
 */
export const storeLicenseKey = (licenseKey: string): void => {
  if (typeof window !== "undefined" && window.localStorage) {
    localStorage.setItem(LICENSE_KEY_STORAGE, licenseKey);
  }
};

/**
 * Retrieve stored license key from localStorage
 */
export const getStoredLicenseKey = (): string | null => {
  if (typeof window !== "undefined" && window.localStorage) {
    return localStorage.getItem(LICENSE_KEY_STORAGE);
  }
  return null;
};

/**
 * Clear stored license key
 */
export const clearLicenseKey = (): void => {
  if (typeof window !== "undefined" && window.localStorage) {
    localStorage.removeItem(LICENSE_KEY_STORAGE);
  }
};

/**
 * Validate a license key against the Vandromeda API
 */
export const validateLicense = async (
  licenseKey: string,
): Promise<LicenseValidationResponse> => {
  const domain = getCurrentDomain();

  try {
    const response = await fetch(`${VANDROMEDA_API_BASE}/license/validate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        license_key: licenseKey,
        domain: domain,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        valid: false,
        tier: "free" as LicenseTier,
        expires_at: "",
        domains_remaining: 0,
        error: errorData.error || `HTTP error ${response.status}`,
      };
    }

    const data: LicenseValidationResponse = await response.json();
    return data;
  } catch (error) {
    debug.error("License validation error:", error);
    return {
      valid: false,
      tier: "free" as LicenseTier,
      expires_at: "",
      domains_remaining: 0,
      error:
        error instanceof Error ? error.message : "Failed to validate license",
    };
  }
};

/**
 * Activate a license key for the current domain
 */
export const activateLicense = async (
  licenseKey: string,
): Promise<LicenseActivationResponse> => {
  const domain = getCurrentDomain();

  try {
    const response = await fetch(`${VANDROMEDA_API_BASE}/license/activate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        license_key: licenseKey,
        domain: domain,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP error ${response.status}`,
      };
    }

    const data: LicenseActivationResponse = await response.json();

    // Store the license key on successful activation
    if (data.success) {
      storeLicenseKey(licenseKey);
    }

    return data;
  } catch (error) {
    debug.error("License activation error:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to activate license",
    };
  }
};

/**
 * Deactivate a license key from the current domain
 */
export const deactivateLicense = async (
  licenseKey: string,
): Promise<LicenseDeactivationResponse> => {
  const domain = getCurrentDomain();

  try {
    const response = await fetch(`${VANDROMEDA_API_BASE}/license/deactivate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        license_key: licenseKey,
        domain: domain,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP error ${response.status}`,
      };
    }

    const data: LicenseDeactivationResponse = await response.json();

    // Clear the stored license key on successful deactivation
    if (data.success) {
      clearLicenseKey();
    }

    return data;
  } catch (error) {
    debug.error("License deactivation error:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to deactivate license",
    };
  }
};

/**
 * Get today's date string for route counting
 */
const getTodayDateString = (): string => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Get the daily route count from localStorage
 */
export const getDailyRouteCount = (): number => {
  if (typeof window === "undefined" || !window.localStorage) {
    return 0;
  }

  const storedDate = localStorage.getItem(ROUTE_COUNT_DATE_STORAGE);
  const today = getTodayDateString();

  // Reset count if it's a new day
  if (storedDate !== today) {
    localStorage.setItem(ROUTE_COUNT_DATE_STORAGE, today);
    localStorage.setItem(ROUTE_COUNT_STORAGE, "0");
    return 0;
  }

  const count = localStorage.getItem(ROUTE_COUNT_STORAGE);
  return count ? parseInt(count, 10) : 0;
};

/**
 * Increment the daily route count
 */
export const incrementDailyRouteCount = (): number => {
  if (typeof window === "undefined" || !window.localStorage) {
    return 0;
  }

  const today = getTodayDateString();
  localStorage.setItem(ROUTE_COUNT_DATE_STORAGE, today);

  const currentCount = getDailyRouteCount();
  const newCount = currentCount + 1;
  localStorage.setItem(ROUTE_COUNT_STORAGE, newCount.toString());

  return newCount;
};

/**
 * Check if the user can perform a route search based on their license
 */
export const canPerformRouteSearch = (
  isPro: boolean,
  dailyLimit: number,
): { allowed: boolean; remaining: number } => {
  if (isPro) {
    return { allowed: true, remaining: -1 }; // -1 indicates unlimited
  }

  const currentCount = getDailyRouteCount();
  const remaining = dailyLimit - currentCount;

  return {
    allowed: remaining > 0,
    remaining: Math.max(0, remaining),
  };
};
